import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import MenuSlideout from '../MenuSlideout';

import {
  Accordion,
  AccordionBtn,
  AccordionBody,
  SlideOutWrapper,
  SlideOutList,
} from './styles';

// * Data {obj} the content obj that is pulled form CMS
// * onClick {func} is a func passed from the header. It sets the label in state
// * label {string} is the id to track the menu by
// * isOpen {bool} to that tracks when the dropdown menu is open

function MenuItem({ data, isOpen, onClick, label, resetState, font, lang }) {
  // * destructuring data
  const { text, subitems } = data;

  // * Reference to the dropdown, so we can access its height
  const dropdownRef = useRef(null);

  // * Tracking the height of the slideout window
  const [slideHeight, setSlideHeight] = useState('auto');

  // * Tracking the height of the dropdown (This matters if the dropdown height is larger then the slideout)
  const [dropDownHeight, setDropDownHeight] = useState('auto');

  const [appliedHeight, setAppliedHeight] = useState('auto');

  // * Func called when clicked. Sets the label in the header, and sets the menu to be open
  const openAccordion = () => {
    onClick(label);
  };

  // * If menu is larger then slideheight, return the menu height
  // * If slideheight is larger then menu height set it to the slideheight, but
  // * 30 is the value of the menu padding, that is why it is removed to get the menu height, and then added to the slide to make sure its centered
  // ! 30 should be set into a variable and passed as the padding into the styled component
  const returnHeight = (menuHeight, slideoutHeight) => {
    const newHeight =
      slideoutHeight > menuHeight - 30 ? `${slideoutHeight + 30}px` : 'auto';
    return newHeight;
  };

  // * State to track which slideouts are open
  const [openSlides, setOpenSlides] = useState({});

  // * When a slice is clicked, capture its label in state
  const onSlideClick = (slideLabel) => {
    const slideIsOpen = !!openSlides[slideLabel];

    // * SlideIsOpen is true when we close the slideout. So we want the menu to use its default height
    if (slideIsOpen) {
      setAppliedHeight('auto');
    } else {
      setAppliedHeight(returnHeight(dropDownHeight, slideHeight));
    }

    setOpenSlides({
      [slideLabel]: !slideIsOpen,
    });
  };

  // * When a menu is opened, set its height in state
  const setMenuHeight = () => {
    if (dropdownRef.current && isOpen) {
      const height = dropdownRef.current.clientHeight;
      setDropDownHeight(height);
    }
  };

  // * wehenever the menu state changes we need to capture the height of the dropdown
  // * We do this to check the dropdown height against the slideout Height. If the slideout height is greater, we must set the dropdown to that height
  useEffect(() => {
    setOpenSlides({});
    setMenuHeight();

    return () => setAppliedHeight('auto');
  }, [isOpen]);

  useEffect(() => {
    setAppliedHeight(returnHeight(dropDownHeight, slideHeight));
  }, [dropDownHeight, slideHeight]);

  return (
    <Accordion className={isOpen ? 'is-open' : ''}>
      <AccordionBtn type="button" onClick={openAccordion} font={font}>
        {text}
      </AccordionBtn>
      <AccordionBody>
        <SlideOutWrapper
          className={Object.values(openSlides).includes(true) ? 'is-open' : ''}
          height={appliedHeight}
          ref={dropdownRef}
        >
          <SlideOutList className="no-bullets" isOpen={openSlides.length !== 0}>
            {subitems.map((item, index) => (
              <MenuSlideout
                key={`slideout-${text}-${item.text}-${index.toString()}`}
                data={item}
                onMount={setSlideHeight}
                slideIsOpen={!!openSlides[`${text}-${item.text}`]}
                slideLabel={`${text}-${item.text}`}
                onSlideClick={onSlideClick}
                openSlides={openSlides}
                resetState={resetState}
                lang={lang}
              />
            ))}
          </SlideOutList>
        </SlideOutWrapper>
      </AccordionBody>
    </Accordion>
  );
}

MenuItem.propTypes = {
  data: PropTypes.shape().isRequired,
  isOpen: PropTypes.bool,
  onClick: PropTypes.func,
  label: PropTypes.string,
  font: PropTypes.string,
  resetState: PropTypes.func,
  lang: PropTypes.string,
};

MenuItem.defaultProps = {
  isOpen: false,
  onClick: () => {},
  label: null,
  resetState: null,
  font: null,
  lang: 'en-ca',
};

export default MenuItem;
